import * as React from 'react';
import { Loader } from 'Src/components/Loader/Loader';
import { superAdminGet, superAdminPost, superAdminDelete } from 'Src/helpers/service/superadmin/api';
import { getSuperAdminSchools } from 'Src/helpers/service/superadmin/schools';

interface UserInvite {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  status?: string;
  inviteKey?: string;
  permission?: {
    preset?: string;
    schoolId?: string;
    comment?: string;
  };
  createdAt?: string;
}

interface InviteForm {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  preset: string;
  schoolId: string;
  comment: string;
}

const EMPTY_FORM: InviteForm = {
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  preset: 'PARENT',
  schoolId: '',
  comment: ''
};

const PRESETS = [
  'ADMIN',
  'MANAGER',
  'TEACHER',
  'COACH',
  'STUDENT',
  'PARENT',
  'PUBLIC_BLOGGER',
  'TOURNAMENT_FOLLOWER',
  'GOVERNOR'
];

const STATUS_BADGE: Record<string, string> = {
  ACTIVE: 'badge-success',
  PENDING: 'badge-warning',
  REMOVED: 'badge-secondary',
  BLOCKED: 'badge-danger'
};

function getInvites(skip = 0): Promise<UserInvite[]> {
  return superAdminGet('/users/invites', {
    params: { filter: { where: { status: { $ne: 'REMOVED' } }, limit: 50, skip, order: 'createdAt DESC' } }
  });
}

function createInvite(data: any): Promise<UserInvite> {
  return superAdminPost('/users/invites', data);
}

function deleteInvite(inviteId: string): Promise<void> {
  return superAdminDelete(`/users/invites/${inviteId}`);
}

interface Props {}

interface State {
  isLoading: boolean;
  isSaving: boolean;
  invites: UserInvite[];
  currentPage: number;
  hasMore: boolean;
  errorMessage: string;
  successMessage: string;
  showForm: boolean;
  form: InviteForm;
  formErrors: Partial<InviteForm>;
  schoolSearch: string;
  schoolOptions: Array<{ id: string; name: string }>;
  isSearchingSchools: boolean;
  deletingId: string | null;
}

export class SuperAdminInvites extends React.Component<Props, State> {
  schoolSearchTimer: any;

  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false,
      isSaving: false,
      invites: [],
      currentPage: 0,
      hasMore: false,
      errorMessage: '',
      successMessage: '',
      showForm: false,
      form: { ...EMPTY_FORM },
      formErrors: {},
      schoolSearch: '',
      schoolOptions: [],
      isSearchingSchools: false,
      deletingId: null
    };
  }

  componentDidMount() {
    this.load(0);
  }

  load = (page: number) => {
    this.setState({ isLoading: true, errorMessage: '' });
    getInvites(page * 50)
      .then(invites => this.setState({ isLoading: false, invites, currentPage: page, hasMore: invites.length === 50 }))
      .catch(() => this.setState({ isLoading: false, errorMessage: 'Could not load invites.' }));
  };

  onSchoolSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    this.setState({ schoolSearch: val, form: { ...this.state.form, schoolId: '' } });
    clearTimeout(this.schoolSearchTimer);
    if (val.trim().length < 2) {
      this.setState({ schoolOptions: [] });
      return;
    }
    this.schoolSearchTimer = setTimeout(() => {
      this.setState({ isSearchingSchools: true });
      getSuperAdminSchools({ where: { name: { like: val, options: 'i' } }, limit: 10, skip: 0 })
        .then(schools =>
          this.setState({
            isSearchingSchools: false,
            schoolOptions: schools.map(s => ({ id: s.id, name: s.name || '' }))
          })
        )
        .catch(() => this.setState({ isSearchingSchools: false }));
    }, 300);
  };

  onSchoolSelect = (school: { id: string; name: string }) => {
    this.setState({ form: { ...this.state.form, schoolId: school.id }, schoolSearch: school.name, schoolOptions: [] });
  };

  onFormChange = (field: keyof InviteForm, value: string) => {
    this.setState({
      form: { ...this.state.form, [field]: value },
      formErrors: { ...this.state.formErrors, [field]: undefined }
    });
  };

  validate(): boolean {
    const { form } = this.state;
    const errors: Partial<InviteForm> = {};
    if (!form.email.trim()) errors.email = 'Required';
    if (!form.schoolId.trim()) errors.schoolId = 'Required — select a school';
    if (!form.preset) errors.preset = 'Required';
    this.setState({ formErrors: errors });
    return Object.keys(errors).length === 0;
  }

  onSubmit = () => {
    if (!this.validate()) return;
    const { form } = this.state;
    const data = {
      email: form.email.trim(),
      ...(form.firstName.trim() && { firstName: form.firstName.trim() }),
      ...(form.lastName.trim() && { lastName: form.lastName.trim() }),
      ...(form.phone.trim() && { phone: form.phone.trim() }),
      permission: {
        preset: form.preset,
        schoolId: form.schoolId,
        ...(form.comment.trim() && { comment: form.comment.trim() })
      }
    };
    this.setState({ isSaving: true, errorMessage: '' });
    createInvite(data)
      .then(() => {
        this.setState({
          isSaving: false,
          showForm: false,
          form: { ...EMPTY_FORM },
          schoolSearch: '',
          successMessage: `Invite sent to ${form.email}`
        });
        this.load(0);
      })
      .catch(() => this.setState({ isSaving: false, errorMessage: 'Could not create invite.' }));
  };

  onDelete = (invite: UserInvite) => {
    if (!window.confirm(`Remove invite for ${invite.email}?`)) return;
    this.setState({ deletingId: invite.id, errorMessage: '' });
    deleteInvite(invite.id)
      .then(() => {
        this.setState({ deletingId: null, successMessage: 'Invite removed.' });
        this.load(this.state.currentPage);
      })
      .catch(() => this.setState({ deletingId: null, errorMessage: 'Could not remove invite.' }));
  };

  renderForm() {
    const { form, formErrors, isSaving, schoolSearch, schoolOptions, isSearchingSchools } = this.state;
    return (
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>New invite</strong>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() =>
              this.setState({ showForm: false, form: { ...EMPTY_FORM }, schoolSearch: '', formErrors: {} })
            }
          >
            Cancel
          </button>
        </div>
        <div className="card-body">
          <div className="form-row">
            <div className="col-md-4 form-group">
              <label className="small font-weight-bold">
                Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className={`form-control form-control-sm ${formErrors.email ? 'is-invalid' : ''}`}
                value={form.email}
                onChange={e => this.onFormChange('email', e.target.value)}
                disabled={isSaving}
              />
              {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
            </div>
            <div className="col-md-3 form-group">
              <label className="small font-weight-bold">First name</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={form.firstName}
                onChange={e => this.onFormChange('firstName', e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className="col-md-3 form-group">
              <label className="small font-weight-bold">Last name</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={form.lastName}
                onChange={e => this.onFormChange('lastName', e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className="col-md-2 form-group">
              <label className="small font-weight-bold">Phone</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={form.phone}
                onChange={e => this.onFormChange('phone', e.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="col-md-5 form-group" style={{ position: 'relative' }}>
              <label className="small font-weight-bold">
                School <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control form-control-sm ${formErrors.schoolId ? 'is-invalid' : ''}`}
                value={schoolSearch}
                onChange={this.onSchoolSearchChange}
                placeholder="Type to search…"
                disabled={isSaving}
              />
              {formErrors.schoolId && <div className="invalid-feedback">{formErrors.schoolId}</div>}
              {form.schoolId && <small className="text-success d-block mt-1">✓ Selected</small>}
              {(schoolOptions.length > 0 || isSearchingSchools) && (
                <div
                  style={{
                    position: 'absolute',
                    zIndex: 100,
                    background: '#fff',
                    border: '1px solid #ced4da',
                    borderRadius: 4,
                    width: '100%',
                    maxHeight: 200,
                    overflowY: 'auto'
                  }}
                >
                  {isSearchingSchools && <div className="p-2 text-muted small">Searching…</div>}
                  {schoolOptions.map(s => (
                    <div
                      key={s.id}
                      className="px-3 py-2 small"
                      style={{ cursor: 'pointer' }}
                      onMouseDown={() => this.onSchoolSelect(s)}
                    >
                      {s.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="col-md-3 form-group">
              <label className="small font-weight-bold">
                Role <span className="text-danger">*</span>
              </label>
              <select
                className="form-control form-control-sm"
                value={form.preset}
                onChange={e => this.onFormChange('preset', e.target.value)}
                disabled={isSaving}
              >
                {PRESETS.map(p => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4 form-group">
              <label className="small font-weight-bold">Comment</label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={form.comment}
                onChange={e => this.onFormChange('comment', e.target.value)}
                disabled={isSaving}
                placeholder="Optional"
              />
            </div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={this.onSubmit} disabled={isSaving}>
            {isSaving ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { isLoading, invites, currentPage, hasMore, errorMessage, successMessage, showForm, deletingId } = this.state;
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Invites</h4>
            <div className="text-muted small">Manage user invites to schools.</div>
          </div>
          {!showForm && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => this.setState({ showForm: true, successMessage: '' })}
            >
              + New invite
            </button>
          )}
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        {showForm && this.renderForm()}

        {isLoading ? (
          <Loader />
        ) : invites.length === 0 ? (
          <div className="text-muted">No invites found.</div>
        ) : (
          <>
            <table className="table table-sm table-bordered table-hover">
              <thead className="thead-light">
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Invite key</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {invites.map(inv => (
                  <tr key={inv.id}>
                    <td className="small">{inv.email || '—'}</td>
                    <td className="small">{[inv.firstName, inv.lastName].filter(Boolean).join(' ') || '—'}</td>
                    <td className="small">
                      {inv.permission?.preset ? <span className="badge badge-info">{inv.permission.preset}</span> : '—'}
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[inv.status || ''] || 'badge-secondary'}`}>
                        {inv.status || '—'}
                      </span>
                    </td>
                    <td className="small text-muted" style={{ fontFamily: 'monospace', fontSize: 11 }}>
                      {inv.inviteKey || '—'}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => this.onDelete(inv)}
                        disabled={deletingId === inv.id}
                      >
                        {deletingId === inv.id ? '…' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="d-flex" style={{ gap: 8 }}>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => this.load(currentPage - 1)}
                disabled={currentPage === 0 || isLoading}
              >
                ← Previous
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => this.load(currentPage + 1)}
                disabled={!hasMore || isLoading}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>
    );
  }
}
